<template>
  <div class="change-credentials-page">
    <div class="content-container">
      <v-card class="credentials-card" elevation="10">
        <!-- Notifications et alertes -->
        <transition name="slide-fade">
          <v-alert v-if="error"
                   dense
                   class="mb-0 rounded-0"
                   color="red"
                   dark
                   border="left">
            {{ error }}
          </v-alert>
        </transition>

        <transition name="slide-fade">
          <v-alert v-if="type"
                   dense
                   class="mb-0 rounded-0"
                   :color="AlertColor(type)"
                   dark
                   border="left"
                   dismissible
                   @input="dismissible_close">
            {{ text }}
          </v-alert>
        </transition>

        <!-- Contenu principal -->
        <div class="card-content">
          <!-- Entête similaire à UserProfile -->
          <div class="profile-header">
            <div class="avatar-container">
              <div class="avatar">
                <v-icon size="36">mdi-account-key</v-icon>
              </div>
            </div>
            <div class="user-name">
              <h2>Modification des accès</h2>
              <div class="user-role">Mot de passe & Code PIN</div>
            </div>
          </div>

          <!-- Formulaire de modification -->
          <v-form ref="form" v-model="valid" @submit.prevent="handleSubmit">
            <!-- Section Matricule (visible uniquement pour admin) -->
            <div v-if="isAdmin" class="section-card mb-3">
              <div class="section-header">
                <v-icon color="primary" x-small>mdi-account-card-details</v-icon>
                <span>Matricule utilisateur</span>
              </div>

              <v-text-field
                  :id="dynamicID()"
                  v-model="form.matricule"
                  :rules="matriculeRules"
                  label="Matricule de l'utilisateur"
                  prepend-inner-icon="mdi-account-badge"
                  color="primary"
                  filled
                  rounded
                  dense
                  background-color="rgba(255, 255, 255, 0.07)"
                  required
                  class="input-field mb-2"
              ></v-text-field>
            </div>

            <!-- Sélection du type de modification -->
            <div class="d-flex justify-space-between mb-5">
              <v-btn
                  :color="changeType === 'password' ? 'primary' : 'grey lighten-1'"
                  :outlined="changeType !== 'password'"
                  @click="changeType = 'password'"
                  class="flex-grow-1 mr-2"
                  rounded
                  height="36"
              >
                <v-icon left>mdi-lock-reset</v-icon>
                Mot de passe
              </v-btn>
              <v-btn
                  :color="changeType === 'pin' ? 'primary' : 'grey lighten-1'"
                  :outlined="changeType !== 'pin'"
                  @click="changeType = 'pin'"
                  class="flex-grow-1 ml-2"
                  rounded
                  height="36"
              >
                <v-icon left>mdi-numeric</v-icon>
                Code PIN
              </v-btn>
            </div>

            <!-- Section Mot de passe -->
            <div v-if="changeType === 'password'" class="section-card mb-3">
              <div class="section-header">
                <v-icon color="primary" x-small>mdi-lock-reset</v-icon>
                <span>Nouveau mot de passe</span>
              </div>

              <v-text-field
                  :id="dynamicID()"
                  v-model="form.password"
                  :rules="passwordRules"
                  :type="show ? 'text' : 'password'"
                  label="Nouveau mot de passe"
                  prepend-inner-icon="mdi-lock-plus"
                  :append-icon="show ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="show = !show"
                  color="primary"
                  filled
                  rounded
                  dense
                  background-color="rgba(255, 255, 255, 0.07)"
                  required
                  class="input-field mb-2"
              ></v-text-field>

              <v-text-field
                  :id="dynamicID()"
                  v-model="form.confirmpassword"
                  :rules="passwordConfirmRules"
                  :type="show ? 'text' : 'password'"
                  label="Confirmation mot de passe"
                  prepend-inner-icon="mdi-lock-check"
                  :append-icon="show ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="show = !show"
                  color="primary"
                  filled
                  rounded
                  dense
                  background-color="rgba(255, 255, 255, 0.07)"
                  required
                  class="input-field"
              ></v-text-field>
            </div>

            <!-- Section Code PIN -->
            <div v-if="changeType === 'pin'" class="section-card mb-3">
              <div class="section-header">
                <v-icon color="primary" x-small>mdi-numeric</v-icon>
                <span>Nouveau code PIN</span>
              </div>

              <v-text-field
                  :id="dynamicID()"
                  v-model="form.pinCode"
                  :rules="pinCodeRules"
                  :type="showPin ? 'text' : 'password'"
                  label="Nouveau code PIN (4 chiffres)"
                  prepend-inner-icon="mdi-numeric"
                  :append-icon="showPin ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="showPin = !showPin"
                  color="primary"
                  filled
                  rounded
                  dense
                  background-color="rgba(255, 255, 255, 0.07)"
                  required
                  maxlength="4"
                  class="input-field mb-2"
              ></v-text-field>

              <v-text-field
                  :id="dynamicID()"
                  v-model="form.confirmPinCode"
                  :rules="pinCodeConfirmRules"
                  :type="showPin ? 'text' : 'password'"
                  label="Confirmation code PIN"
                  prepend-inner-icon="mdi-numeric-9-plus-box"
                  :append-icon="showPin ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="showPin = !showPin"
                  color="primary"
                  filled
                  rounded
                  dense
                  background-color="rgba(255, 255, 255, 0.07)"
                  required
                  maxlength="4"
                  class="input-field"
              ></v-text-field>
            </div>

            <!-- Message d'erreur -->
            <div class="red--text text-center mt-1 mb-2" v-if="errorMessage">{{ errorMessage }}</div>

            <!-- Boutons d'action -->
            <div class="action-buttons mb-2">
              <v-btn
                  v-if="bDisplaySaveButton"
                  :loading="loading"
                  @click.prevent="saveChanges"
                  rounded
                  depressed
                  :color="isFormValid ? 'primary' : 'grey lighten-1'"
                  :dark="isFormValid"
                  :disabled="!isFormValid"
                  elevation="2"
                  class="action-button my-2"
                  height="44"
                  block
              >
                <v-icon left>mdi-content-save</v-icon>
                Enregistrer les modifications
              </v-btn>

              <v-btn
                  @click="goBack"
                  rounded
                  depressed
                  color="grey darken-1"
                  dark
                  elevation="2"
                  class="action-button my-2"
                  height="44"
                  block
              >
                <v-icon left>mdi-arrow-left</v-icon>
                Retour
              </v-btn>
            </div>
          </v-form>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import apiClient from "../../../config/api.js";

// Pour le débogage
console.log("Composant ChangeCredentials chargé");

function isMobileDevice() {
  return /Mobi|Android/i.test(navigator.userAgent);
}

const getRandomInteger = (min, max) => {
  min = Math.ceil(min)
  max = Math.floor(max)

  return Math.floor(Math.random() * (max - min)) + min
}

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func.apply(this, args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default {
  data() {
    return {
      // Options de base
      changeType: 'password', // 'password' ou 'pin'
      bDisplaySaveButton: true,
      bDisplayEnvoiCode: false, // Ajout pour éviter l'erreur
      bDisplayCodeConfirm: false, // Ajout pour éviter l'erreur
      showCurrentPwd: false,
      show: false,
      showPin: false,

      // États de validation
      bMatriculeOK: false,
      bCurrentPwdOK: false,
      bPwdOK: false,
      bPwdOKConfirm: false,
      bPinOK: false,
      bPinOKConfirm: false,

      // Valeurs du formulaire
      form: {
        matricule: "",
        currentPassword: "",
        password: "",
        confirmpassword: "",
        pinCode: "",
        confirmPinCode: "",
        baseUrl: process.env.VUE_APP_ENVIRONMENT || "Local" // Valeur par défaut
      },

      // Règles de validation
      matriculeRules: [
        v => v !== null && v !== undefined || 'Le matricule est obligatoire.',
        v => {
          if (v === null || v === undefined) return true; // Déjà géré par la règle précédente
          return (/^[0-9]{5}$/.test(v) || v.toLowerCase().includes("admin")) || 'Matricule invalide';
        }
      ],
      currentPasswordRules: [
        v => v !== null && v !== undefined || 'Le mot de passe actuel est obligatoire.',
      ],
      passwordRules: [
        v => v !== null && v !== undefined || 'Le mot de passe est obligatoire.',
        v => {
          if (v === null || v === undefined) return true;
          return /\d/.test(v) || 'Le mot de passe doit contenir au moins 1 chiffre.';
        },
        v => {
          if (v === null || v === undefined) return true;
          return /[A-Z]{1}/.test(v) || 'Le mot de passe doit contenir au moins 1 majuscule.';
        },
        v => {
          if (v === null || v === undefined) return true;
          return /[a-z]{1}/.test(v) || 'Le mot de passe doit contenir au moins 1 minuscule.';
        },
        v => {
          if (v === null || v === undefined) return true;
          return v.length > 5 || 'Le mot de passe doit contenir au minimum 6 caractères.';
        },
        v => {
          if (v === null || v === undefined) return true;
          return v !== this.form.currentPassword || 'Le nouveau mot de passe doit être différent de l\'actuel.';
        }
      ],
      passwordConfirmRules: [
        v => v !== null && v !== undefined || 'La confirmation du mot de passe est obligatoire.',
        v => {
          if (v === null || v === undefined) return true;
          return v === this.form.password || 'Les mots de passe ne correspondent pas.';
        }
      ],
      pinCodeRules: [
        v => v !== null && v !== undefined || 'Le code PIN est obligatoire.',
        v => {
          if (v === null || v === undefined) return true;
          return /^\d{4}$/.test(v) || 'Le code PIN doit contenir exactement 4 chiffres.';
        },
        v => {
          if (v === null || v === undefined) return true;
          const simplePINs = [
            '0000', '1111', '2222', '3333', '4444', '5555', '6666', '7777', '8888', '9999',
            '1234', '2345', '3456', '4567', '5678', '6789', '0123',
            '4321', '5432', '6543', '7654', '8765', '9876', '3210'
          ];
          return !simplePINs.includes(v) || 'Ce code PIN est trop simple. Choisissez un code plus sécurisé.';
        }
      ],
      pinCodeConfirmRules: [
        v => v !== null && v !== undefined || 'La confirmation du code PIN est obligatoire.',
        v => {
          if (v === null || v === undefined) return true;
          return v === this.form.pinCode || 'Les codes PIN ne correspondent pas.';
        }
      ],

      // API et authentification
      token: "",
      baseUrlTmp: "",

      // UI et messages
      isMobile: isMobileDevice(),
      valid: false,
      type: null,
      text: null,
      error: null,
      errorMessage: "",
      loading: false
    };
  },
  computed: {
    isFormValid() {
      if (this.changeType === 'password') {
        return this.bPwdOK && this.bPwdOKConfirm;
      } else { // PIN
        return this.bPinOK && this.bPinOKConfirm;
      }
    },
    isAdmin() {
      // Vérifier si l'utilisateur est admin
      const userId = sessionStorage.getItem("userId");
      return userId && userId.toString().toLowerCase() === "admin";
    }
  },
  created() {
    // Initialiser le matricule dès la création du composant
    const storedUserId = sessionStorage.getItem("userId");
    if (storedUserId) {
      // Si c'est un admin, ne pas préremplir le matricule
      if (storedUserId.toString().toLowerCase() === "admin") {
        this.form.matricule = "";
        this.bMatriculeOK = false;
      } else {
        this.form.matricule = storedUserId;
        this.bMatriculeOK = true;
      }
    }

    console.log("Composant initialisé avec matricule:", this.form.matricule);
  },
  // Méthode appelée lors du montage du composant
  mounted() {
    // Pré-remplir avec les informations de l'utilisateur connecté si disponible
    const user = this.$store.getters.user;
    // Ne pré-remplir que si l'utilisateur n'est pas admin
    if (user && user.userId && !this.isAdmin) {
      this.form.matricule = user.userId;
      this.bMatriculeOK = true;
    }

    // Utiliser l'URL stockée dans le store ou déterminer selon l'environnement
    this.baseUrlTmp = this.$store.getters.user.baseUrl || this.getBaseUrl();
  },
  watch: {
    // Surveiller le champ matricule
    'form.matricule': function (val) {
      // Vérifier si le matricule est valide (5 chiffres ou contient "admin")
      this.bMatriculeOK = val && (/^[0-9]{5}$/.test(val) || val.toLowerCase().includes("admin"));

      // Réinitialiser le message d'erreur si le champ est valide
      if (this.bMatriculeOK) {
        this.errorMessage = null;
      }
    },

    // Surveiller le champ mot de passe actuel
    'form.currentPassword': function (val) {
      this.bCurrentPwdOK = !!val;

      if (this.bCurrentPwdOK) {
        this.errorMessage = null;
      }
    },

    // Surveiller le champ nouveau mot de passe
    'form.password': function (val) {
      // Vérifier si le mot de passe respecte tous les critères
      this.bPwdOK = val &&
          /\d/.test(val) &&
          /[A-Z]{1}/.test(val) &&
          /[a-z]{1}/.test(val) &&
          val.length > 5;

      // Réinitialiser le message d'erreur si le champ est valide
      if (this.bPwdOK) {
        this.errorMessage = null;
      }

      // Vérifier également la confirmation de mot de passe si elle existe déjà
      if (this.form.confirmpassword) {
        this.bPwdOKConfirm = this.form.confirmpassword === val;
      }
    },

    // Surveiller le champ de confirmation du mot de passe
    'form.confirmpassword': function (val) {
      // Vérifier si la confirmation correspond au mot de passe
      this.bPwdOKConfirm = val && val === this.form.password;

      // Réinitialiser le message d'erreur si le champ est valide
      if (this.bPwdOKConfirm) {
        this.errorMessage = null;
      }
    },

    // Surveiller le champ code PIN
    'form.pinCode': function (val) {
      // Vérifier si le code PIN contient exactement 4 chiffres
      this.bPinOK = val && /^\d{4}$/.test(val);

      // Réinitialiser le message d'erreur si le champ est valide
      if (this.bPinOK) {
        this.errorMessage = null;
      }

      // Vérifier également la confirmation du PIN si elle existe déjà
      if (this.form.confirmPinCode) {
        this.bPinOKConfirm = this.form.confirmPinCode === val;
      }
    },

    // Surveiller le champ de confirmation du code PIN
    'form.confirmPinCode': function (val) {
      // Vérifier si la confirmation correspond au code PIN
      this.bPinOKConfirm = val && val === this.form.pinCode;

      // Réinitialiser le message d'erreur si le champ est valide
      if (this.bPinOKConfirm) {
        this.errorMessage = null;
      }
    },

    // Surveiller les changements de type de modification
    'changeType': function (val) {
      // Réinitialiser les validations spécifiques
      if (val === 'password') {
        this.bPinOK = false;
        this.bPinOKConfirm = false;
        this.form.pinCode = "";
        this.form.confirmPinCode = "";
      } else {
        this.bPwdOK = false;
        this.bPwdOKConfirm = false;
        this.form.password = "";
        this.form.confirmpassword = "";
      }

      // Réinitialiser le message d'erreur
      this.errorMessage = null;
    }
  },
  methods: {
    // Helper method to get base URL from environment or fallback to hardcoded logic
    getBaseUrl(environment = null) {
      const env = environment || this.form.baseUrl;
      
      // First try to get from environment variable
      if (process.env.VUE_APP_BASE_URL) {
        return process.env.VUE_APP_BASE_URL;
      }
      
      // Fallback to hardcoded logic
      const baseUrls = {
        "Prod": "https://tsarh.cartaware.cartalink.com",
        "Test": "https://tests.eyone.cartaware.cartalink.com",
        "Local": "http://localhost:8082"
      };
      
      return baseUrls[env] || baseUrls["Local"];
    },

    handleSubmit() {
      this.valid = this.$refs.form.validate()
    },
    isNumber(value) {
      return (value && /^[0-9]+$/.test(value));
    },
    AlertColor(type) {
      return type === "info" ? "#4CAF50" : "#F44336";
    },
    showAlert(type, timout) {
      this.type = type

      let timer = this.showAlert.timer
      if (timer) {
        clearTimeout(timer)
      }
      this.showAlert.timer = setTimeout(() => {
        this.type = null
      }, timout)

      this.elapse = 1
      let t = this.showAlert.t
      if (t) {
        clearInterval(t)
      }

      this.showAlert.t = setInterval(() => {
        if (this.elapse === 3) {
          this.elapse = 0
          clearInterval(this.showAlert.t)
        } else {
          this.elapse++
        }
      }, timout)
    },
    dismissible_close() {
      this.elapse = 3
      clearTimeout(this.showAlert.timer)
      clearInterval(this.showAlert.t)
      this.type = null
    },
    dynamicID() {
      return 'dynamicID-' + Math.floor(Math.random() * Date.now().toString());
    },
    goBack() {
      // Retourner à la page précédente ou à une route spécifique
      this.$router.go(-1);
    },
    saveChanges() {
      if (!this.isFormValid) {
        return;
      }

      // Vérifier si le matricule est fourni
      if (!this.form.matricule || this.form.matricule.trim() === "") {
        this.errorMessage = "Veuillez saisir un matricule valide";
        return;
      }

      this.loading = true;

      // Procéder directement aux modifications sans vérification du mot de passe actuel
      this.processChanges();
    },
    processChanges() {
      // Variable pour suivre les modifications effectuées
      let successMessage = "";
      let errorMessage = "";
      let promises = [];

      // S'assurer que le matricule est défini
      if (!this.form.matricule || this.form.matricule.trim() === "") {
        this.text = "Erreur: Matricule non défini";
        this.showAlert('error', 5000);
        this.loading = false;
        return;
      }

      // Modification du mot de passe si nécessaire
      if (this.changeType === 'password') {
        const passwordPromise = apiClient
            .post(
                `${this.baseUrlTmp}/account/user/passwordbyuser/${this.form.matricule}/${this.form.password}/Admin`
            )
            .then(response => {
              if (response.data.status === 200) {
                successMessage += "Mot de passe modifié avec succès. ";

                // Enregistrer le changement de mot de passe sans code de vérification
                return apiClient.post(
                    `${this.baseUrlTmp}/account/savechangepassword/${this.form.matricule}/DIRECT/${this.form.password}/DIRECT/${this.form.matricule}`
                );
              } else {
                errorMessage += "Échec de la modification du mot de passe. ";
                return Promise.reject("Échec modification mot de passe");
              }
            })
            .catch(error => {
              console.error("Erreur lors de la modification du mot de passe:", error);
              errorMessage += "Échec de la modification du mot de passe. ";
              return Promise.reject("Échec modification mot de passe");
            });

        promises.push(passwordPromise);
      }

      // Modification du code PIN si nécessaire
      if (this.changeType === 'pin') {
        const pinPromise = apiClient
            .post(
                `${this.baseUrlTmp}/account/user/pincode_by_matricule/${this.form.matricule}/${this.form.pinCode}/Admin`
            )
            .then(response => {
              if (response.data.status === 200) {
                successMessage += "Code PIN modifié avec succès. ";
              } else {
                errorMessage += "Échec de la modification du code PIN. ";
                return Promise.reject("Échec modification PIN");
              }
            })
            .catch(error => {
              console.error("Erreur lors de la modification du code PIN:", error);
              errorMessage += "Échec de la modification du code PIN. ";
              return Promise.reject("Échec modification PIN");
            });

        promises.push(pinPromise);
      }

      // Traiter tous les changements
      Promise.allSettled(promises)
          .then(results => {
            this.loading = false;
            if (successMessage) {
              this.text = successMessage;
              this.showAlert('info', 5000);

              // Réinitialiser le formulaire après 5 secondes
              setTimeout(() => {
                this.resetForm();
              }, 5000);
            } else {
              this.text = errorMessage || "Aucune modification effectuée.";
              this.showAlert('error', 5000);
            }
          })
          .catch(() => {
            this.loading = false;
            this.text = errorMessage || "Erreur lors des modifications.";
            this.showAlert('error', 5000);
          });
    },
    resetForm() {
      // Ne pas effacer le matricule si l'utilisateur est connecté et n'est pas admin
      const storedUserId = sessionStorage.getItem("userId");
      if (!storedUserId || storedUserId.toString().toLowerCase() === "admin") {
        this.form.matricule = "";
        this.bMatriculeOK = false;
      } else if (storedUserId) {
        this.form.matricule = storedUserId;
        this.bMatriculeOK = true;
      }

      this.form.currentPassword = "";
      this.form.password = "";
      this.form.confirmpassword = "";
      this.form.pinCode = "";
      this.form.confirmPinCode = "";

      this.bCurrentPwdOK = false;
      this.bPwdOK = false;
      this.bPwdOKConfirm = false;
      this.bPinOK = false;
      this.bPinOKConfirm = false;
    },
    digitSum(n) {
      // Vérifier si n est null, undefined ou vide
      if (n === null || n === undefined || n === '') {
        return 0;
      }

      // Si n est une chaîne, vérifier si elle contient uniquement des chiffres
      if (typeof n === 'string') {
        // Rejeter toute chaîne qui n'est pas composée uniquement de chiffres
        if (!/^\d+$/.test(n)) {
          return 0;
        }
        // Convertir la chaîne numérique en nombre
        n = parseInt(n, 10);
      }

      // Si n n'est pas un nombre à ce stade, rejeter
      if (typeof n !== 'number' || isNaN(n)) {
        return 0;
      }

      // Version itérative (non récursive) pour éviter les débordements de pile
      let num = n;
      while (num >= 10) {
        let sum = 0;
        while (num > 0) {
          sum += num % 10;
          num = Math.floor(num / 10);
        }
        num = sum;
      }

      return num;
    },
    getDay(date) {
      if (!date) return null

      const [year, month, day] = date.split('-')
      return `${day}`
    },
    getHour(time) {
      if (!time) return null

      const [hh, mm, ss] = time.split(':')
      return `${hh}`
    },
    isEmpty(str) {
      return (str === null || !str || 0 === str.length || /^\s*$/.test(str));
    },
  }
}
</script>

<style scoped>
/* Variables CSS pour les couleurs principales */
:root {
  --primary-color: #01579B;
  --secondary-color: #0288D1;
  --text-color: #333;
  --bg-light: rgba(255, 255, 255, 0.95);
}

.change-credentials-page {
  min-height: 100vh;
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
}

.content-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
  z-index: 2;
}

.credentials-card {
  border-radius: 12px;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.99) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  max-width: 450px;
  margin: 0 auto;
  width: 100%;
}

.card-content {
  padding: 15px;
}

/* Styles pour l'en-tête de profil (comme UserProfile) */
.profile-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px;
  background: linear-gradient(135deg, #01579B 0%, #0288D1 100%);
  border-radius: 8px;
  color: white;
}

.avatar-container {
  position: relative;
  margin-right: 20px;
}

.avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: white;
  color: #01579B;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.user-name {
  flex: 1;
}

.user-name h2 {
  margin: 0 0 5px 0;
  font-size: 20px;
  font-weight: 600;
}

.user-role {
  font-size: 14px;
  opacity: 0.9;
  font-weight: 500;
}

.input-field {
  border-radius: 12px;
  transition: all 0.3s ease;
}

.input-field >>> input {
  text-align: center;
}

.input-field >>> .v-input__slot {
  min-height: 50px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
  transition: all 0.3s ease;
}

.input-field >>> .v-input__slot:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.section-card {
  background-color: rgba(1, 87, 155, 0.03);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
  border-left: 3px solid #01579B;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  color: #01579B;
  font-weight: 600;
  font-size: 13px;
}

.action-button {
  letter-spacing: 1px !important;
  transition: all 0.3s ease;
  text-transform: none;
  font-weight: 600;
  font-size: 15px;
}

.v-btn {
  font-weight: 500;
  letter-spacing: 0.5px;
}

.action-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(1, 87, 155, 0.25) !important;
}

/* Animation de transition pour les alertes */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter, .slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

/* Styles pour les appareils mobiles */
@media (max-width: 600px) {
  .card-content {
    padding: 15px;
  }

  .credentials-card {
    border-radius: 12px;
    margin: 16px;
  }

  .section-card {
    padding: 12px;
  }

  .profile-header {
    flex-direction: column;
    text-align: center;
  }

  .avatar-container {
    margin-right: 0;
    margin-bottom: 15px;
  }
}

/* Style spécifique pour les champs auto-remplis par les navigateurs */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px rgba(255, 255, 255, 0.1) inset !important;
  -webkit-text-fill-color: inherit !important;
  transition: background-color 5000s ease-in-out 0s;
}
</style>